import React, { FC, ReactElement } from 'react';
import { chunk } from 'lodash';
import { formatNumber } from 'humanize-plus';
import { titleize } from 'inflection';
import moment from 'moment';

import { StyleProps } from 'buyside/components/presentation/IdeaShowPage/InfoTable/infoTable';
import { Maybe, UserCreditData } from 'interfaces/graphql-typings';

import styles from 'buyside/components/presentation/IdeaShowPage/RightSidebar/PositionInformation/positionInformationStyles.scss';

const IndentureData: FC<StyleProps & {
  currencyCode: string;
  userCreditData: Maybe<
    Pick<
      UserCreditData,
      | 'amountOutstanding'
      | 'convertible'
      | 'guaranteed'
      | 'issueSize'
      | 'lien'
      | 'maturityDate'
      | 'securedBy'
      | 'securityClass'
      | 'seniority'
    >
  >;
}> = ({ currencyCode, dataStyle, labelStyle, userCreditData }) => {
  const {
    amountOutstanding,
    convertible,
    guaranteed,
    issueSize,
    lien,
    maturityDate,
    securedBy,
    securityClass,
    seniority
  } = userCreditData || {};

  const isUnsecured = !securityClass || securityClass === 'unsecured';

  const indentureItems = [
    {
      label: 'SECURITY CLASS',
      data: securityClass ? `${titleize(securityClass)}*` : 'N/A'
    },
    isUnsecured
      ? null
      : {
          label: 'LIEN',
          data: lien ? `${titleize(lien)}*` : 'N/A'
        },
    isUnsecured
      ? null
      : {
          label: 'SECURED BY',
          data: securedBy ? `${securedBy}*` : 'N/A'
        },
    {
      label: 'SENIORITY',
      data: seniority ? `${titleize(seniority)}*` : 'N/A'
    },
    { label: 'GUARANTEED', data: guaranteed ? 'Yes*' : 'No*' },
    { label: 'CONVERTIBLE', data: convertible ? 'Yes*' : 'No*' },
    {
      label: 'MATURITY DATE',
      data: maturityDate ? `${moment(maturityDate).format('M/D/YY')}*` : 'N/A'
    },
    {
      label: 'ISSUE SIZE',
      data: issueSize ? `${formatNumber(issueSize, 2)} ${currencyCode}*` : 'N/A'
    },
    {
      label: 'AMOUNT OUTSTANDING',
      data: amountOutstanding ? `${formatNumber(amountOutstanding, 2)} ${currencyCode}*` : 'N/A'
    }
  ].filter((el): boolean => Boolean(el)) as { label: string; data: string }[];

  return (
    <table className={styles.table}>
      <tbody>
        {chunk(indentureItems, 2).map(
          ([first, second], idx): ReactElement => (
            <tr key={idx}>
              <td>
                <div className={labelStyle}>{first.label}</div>
                <div className={dataStyle}>{first.data}</div>
              </td>
              {second && (
                <td>
                  <div className={labelStyle}>{second.label}</div>
                  <div className={dataStyle}>{second.data}</div>
                </td>
              )}
            </tr>
          )
        )}
      </tbody>
    </table>
  );
};

export default IndentureData;
